// Roster entries. An entry (a `players` doc) is shown by its `name`. It can
// stand for one player or for several people, e.g. a map built by a team in a
// mapping contest. `members` lists those people as
// [{ userId, name, imageUrl, country }]; older docs have none, so the entry's
// own user is its only member. `memberIds` mirrors the user ids for lookups.

export const entryMembers = (player) => {
  if (!player) return [];
  if (Array.isArray(player.members) && player.members.length) return player.members;
  if (!player.userId) return [];
  return [
    {
      userId: player.userId,
      name: player.userName || player.name,
      imageUrl: player.imageUrl || '',
      country: player.country || '',
      countryName: player.countryName || '',
    },
  ];
};

// "by A & B" text for entries whose name isn't just their one member's name.
// null when the name already says who it is.
export const entryByline = (player) => {
  const members = entryMembers(player);
  if (!members.length) return null;
  if (members.length === 1 && members[0].name === player.name) return null;
  const names = members.map((m) => m.name).filter(Boolean);
  if (!names.length) return null;
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
};

export const isEntryMember = (player, uid) =>
  !!uid &&
  (player.userId === uid ||
    player.uid === uid ||
    player.id === uid ||
    (player.memberIds || []).includes(uid) ||
    entryMembers(player).some((m) => m.userId === uid));

// Firestore fields for an entry with this name and these members. The first
// member stays the entry's `userId`, so it keeps showing on their profile.
export const entryFields = (name, members) => {
  const first = members[0] || {};
  return {
    name: name.trim(),
    members: members.map((m) => ({
      userId: m.userId,
      name: m.name || '',
      imageUrl: m.imageUrl || '',
      country: m.country || '',
      countryName: m.countryName || '',
    })),
    memberIds: members.map((m) => m.userId),
    userId: first.userId || null,
    playerProfile: first.userId || null,
    userName: first.name || '',
    imageUrl: first.imageUrl || '',
    country: first.country || '',
    countryName: first.countryName || '',
  };
};

export const ENTRY_NAME_MAX = 60;

export const validateEntry = (name, members) => {
  const errors = [];
  const n = (name || '').trim();
  if (!n) errors.push('Give the entry a name.');
  if (n.length > ENTRY_NAME_MAX) errors.push(`Names can be at most ${ENTRY_NAME_MAX} characters.`);
  if (!members.length) errors.push('Add at least one person.');
  return errors;
};
